import { useEffect, useState } from 'react';
import {
  CancellationWaitlistNotifyMode,
  ReschedulePolicy,
  ReschedulePolicyBehavior,
} from '@prisma/client';
import { WAITLIST_NOTIFY_MODE_LABELS } from '../../../constants/cancellation-waitlist';
import {
  fromFormReschedulePolicy,
  getBusinessCurrency,
  getBusinessShopUrl,
  toFormReschedulePolicy,
} from '../../../helpers/businesses';
import {
  RESCHEDULE_BEHAVIOR_LABELS,
  RESCHEDULE_BEHAVIOR_OPTIONS,
} from '../../../constants/businesses';
import useUser from '../../../hooks/global/useUser';
import { LoadedUser } from '../../../interfaces';
import SettingsSection from '../SettingsSection';
import FormSmallModal, { FormData } from '../../global/FormSmallModal';
import {
  updateBusinessSchema,
  UpdateBusinessSettingsPayload,
  updateBusinessSettingsSchema,
} from '../../../types/businesses';
import SectionDivider from '../SectionDivider';
import { formatDollarAmount } from '../../../utils/global/helper';
import useGetBusinessSettings from '../../../hooks/businesses/useGetBusinessSettings';
import useUpdateBusiness from '../../../hooks/businesses/useUpdateBusiness';
import useUpdateBusinessSettings from '../../../hooks/businesses/useUpdateBusinessSettings';
import ServicesSettings from '../ServicesSettings';
import MinorSettings from '../MinorSettings';
import useConfirm from '../../../hooks/useConfirm';

interface GeneralManagersState {
  managers: string[];
}

interface StationMaxState {
  stationMax: number | null;
}

const TOOLTIP: Record<string, string> = {
  bookAheadLimitDays:
    'How many days into the future should customers be able to schedule?',
  bookInAdvanceHours:
    'How long in advance should customers be required to schedule?',
  maxCancellationTimes:
    'How many times should customers be able to reschedule for free? We will charge a new deposit if they try to reschedule more than this.',
  minCancellationDays:
    'How far in advance do customers need to reschedule? If they try to reschedule past this time, we will charge a new deposit fee.',
  stackDays:
    'Enabling this setting will urge customers to book on days when you already have an appointment',
  stackTimes:
    'Maximize your in-studio days by only showing customers the option to book the first available or last available time slot of the day. Turning this on will ensure smaller appointments are not booked in the middle of your day.',
  maxApptsPerDay: "Limit the employees' number of appointments per day",
  allowCancellations:
    'When disabled, clients will not be able to cancel their appointments. You can still cancel on their behalf.',
  reschedulePolicy:
    'Turn this on to let clients reschedule their own appointments. If turned off, clients will need to contact you directly to make changes.',
  reschedulePolicyBehavior:
    'Choose what happens if a client tries to reschedule after using their free reschedules or within your notice window.',
  rescheduleFee:
    'The flat fee charged when a client reschedules beyond the free limit.',
  cancellationWaitlistEnabled:
    'When enabled, clients can join a waitlist and get notified when a booked slot opens up.',
  cancellationWaitlistNotifyMode:
    'Notify everyone on the waitlist at once (first to book wins), or a few at a time until the slot is booked.',
};

function BookingSettings() {
  const [form, setForm] = useState<FormData | null>(null);

  const confirm = useConfirm();

  const { user } = useUser<LoadedUser>();
  const {
    settings,
    isLoading: isLoadingSettings,
    isEnabled,
  } = useGetBusinessSettings();

  const onSuccess = () => {
    setForm(null);
  };

  const { mutateAsync: updateBusiness } = useUpdateBusiness({
    onSuccess,
  });
  const { mutateAsync: updateBusinessSettings } = useUpdateBusinessSettings({
    onSuccess,
  });

  const [allowGeneralRequests, setAllowGeneralRequests] = useState<boolean>(
    !!user?.business?.allowGeneralRequests,
  );

  useEffect(() => {
    setAllowGeneralRequests(!!user?.business?.allowGeneralRequests);
  }, [user?.business?.allowGeneralRequests]);

  if (!user?.business) return null;

  const { business } = user;

  const shopManagers = business.employees.filter(
    etb => etb.user?.role === 'SHOP_MANAGER',
  );
  const generalRequestManagers = shopManagers.filter(
    manager => manager.generalRequestOwner,
  );
  const generalRequestManagerIds = generalRequestManagers.map(
    etb => etb.employeeId,
  );
  const generalManagersNames = generalRequestManagers.map(
    etb =>
      `${etb.user?.firstName ?? ''} ${etb.user?.lastName ?? ''}`.trim() ||
      'Unnamed',
  );
  const generalManagersString =
    generalManagersNames.join(', ') || 'No managers selected';

  const onToggleAllowGeneralRequests = async (allow: boolean) => {
    setAllowGeneralRequests(allow);
    try {
      const updatedBusiness = await updateBusiness({
        allowGeneralRequests: allow,
      });
      if (updatedBusiness?.allowGeneralRequests !== allow) {
        setAllowGeneralRequests(!!updatedBusiness?.allowGeneralRequests);
      }
    } catch (error) {
      setAllowGeneralRequests(!!user?.business?.allowGeneralRequests);
    }
  };

  const defaultArtistSettingsForm: FormData<UpdateBusinessSettingsPayload> = {
    title: 'Default Artist Settings',
    initialValues: {
      bookAheadLimitDays: settings?.bookAheadLimitDays || 0,
      bookInAdvanceHours: settings?.bookInAdvanceMinutes
        ? settings.bookInAdvanceMinutes / 60
        : 0,
      maxCancellationTimes: settings?.maxCancellationTimes || 0,
      minAppointmentPrice: settings?.minAppointmentPrice || 0,
      minCancellationDays: settings?.minCancellationDays || 0,
      stackDays: !!settings?.stackDays,
      stackTimes: !!settings?.stackTimes,
      cancellationPolicyText: settings?.cancellationPolicyText || '',
      applyToExistingUsers: false,
      maxApptsPerDay: (settings?.maxApptsPerDay || '') as any,
      allowCancellations: settings?.allowCancellations ?? true,
      reschedulePolicy: toFormReschedulePolicy(settings?.reschedulePolicy),
      reschedulePolicyBehavior:
        settings?.reschedulePolicyBehavior ?? ReschedulePolicyBehavior.DEPOSIT,
      rescheduleFee: settings?.rescheduleFee ?? 0,
      cancellationWaitlistEnabled:
        settings?.cancellationWaitlistEnabled ?? true,
      cancellationWaitlistNotifyMode:
        settings?.cancellationWaitlistNotifyMode ??
        CancellationWaitlistNotifyMode.INSTANT_ALL,
    },
    validationSchema: updateBusinessSettingsSchema,
    inputRows: [
      [
        {
          inputType: 'number',
          label: 'Appointment minimum',
          name: 'minAppointmentPrice',
          placeholder: '0',
          tooltip: TOOLTIP.minAppointmentPrice,
          suffix: getBusinessCurrency(business),
          min: 0,
        },
        {
          inputType: 'number',
          label: 'Max appts. per day',
          name: 'maxApptsPerDay',
          placeholder: 'No max',
          tooltip: TOOLTIP.maxApptsPerDay,
        },
      ],
      [
        {
          inputType: 'number',
          label: 'Book ahead limit',
          name: 'bookAheadLimitDays',
          placeholder: '0',
          tooltip: TOOLTIP.bookAheadLimitDays,
          min: 0,
          suffix: 'days',
        },
        {
          inputType: 'number',
          label: 'Book in advance',
          name: 'bookInAdvanceHours',
          placeholder: '0',
          tooltip: TOOLTIP.bookInAdvanceHours,
          min: 0,
          suffix: 'hours',
        },
      ],
      [
        {
          inputType: 'select',
          label: 'Stack days',
          name: 'stackDays',
          tooltip: TOOLTIP.stackDays,
          options: [
            { value: true, label: 'Yes' },
            { value: false, label: 'No' },
          ],
          autoWidth: false,
        },
        {
          inputType: 'select',
          label: 'Stack times',
          name: 'stackTimes',
          tooltip: TOOLTIP.stackTimes,
          options: [
            { value: true, label: 'Yes' },
            { value: false, label: 'No' },
          ],
          autoWidth: false,
        },
      ],
      [
        {
          inputType: 'select',
          label: 'Allow cancellations',
          name: 'allowCancellations',
          tooltip: TOOLTIP.allowCancellations,
          options: [
            { value: true, label: 'Yes' },
            { value: false, label: 'No' },
          ],
          autoWidth: false,
        },
      ],
      [
        {
          inputType: 'switch',
          label: 'Cancellation waitlist',
          name: 'cancellationWaitlistEnabled',
          tooltip: TOOLTIP.cancellationWaitlistEnabled,
        },
      ],
      [
        {
          inputType: 'select',
          label: 'Waitlist notification style',
          name: 'cancellationWaitlistNotifyMode',
          tooltip: TOOLTIP.cancellationWaitlistNotifyMode,
          options: Object.values(CancellationWaitlistNotifyMode).map(mode => ({
            value: mode,
            label: WAITLIST_NOTIFY_MODE_LABELS[mode],
          })),
          autoWidth: false,
          isVisible: values => !!values.cancellationWaitlistEnabled,
        },
      ],
      [
        {
          inputType: 'textarea',
          label: 'Cancellation policy',
          name: 'cancellationPolicyText',
          placeholder: 'No cancellation policy set',
          tooltip: TOOLTIP.cancellationPolicyText,
        },
      ],
      [
        {
          inputType: 'select',
          label: 'Allow clients to reschedule',
          name: 'reschedulePolicy',
          tooltip: TOOLTIP.reschedulePolicy,
          options: [
            { value: ReschedulePolicy.OPEN, label: 'Yes' },
            { value: ReschedulePolicy.CLOSED, label: 'No' },
          ],
          autoWidth: false,
        },
      ],
      [
        {
          inputType: 'number',
          label: 'Number of free reschedules',
          name: 'maxCancellationTimes',
          placeholder: '0',
          tooltip: TOOLTIP.maxCancellationTimes,
          min: 0,
        },
      ],
      [
        {
          inputType: 'number',
          label: 'Minimum notice to reschedule',
          name: 'minCancellationDays',
          placeholder: '0',
          tooltip: TOOLTIP.minCancellationDays,
          min: 0,
          suffix: 'days',
        },
      ],
      [
        {
          inputType: 'select',
          label: 'Policy when limits are exceeded',
          name: 'reschedulePolicyBehavior',
          tooltip: TOOLTIP.reschedulePolicyBehavior,
          options: RESCHEDULE_BEHAVIOR_OPTIONS,
          autoWidth: false,
        },
      ],
      [
        {
          inputType: 'number',
          label: 'Reschedule fee',
          name: 'rescheduleFee',
          placeholder: '0',
          tooltip: TOOLTIP.rescheduleFee,
          min: 0,
          suffix: getBusinessCurrency(business),
          isVisible: values =>
            values.reschedulePolicyBehavior ===
            ReschedulePolicyBehavior.FLAT_FEE,
        },
      ],
    ],
    onSubmit: async values => {
      const allowCancellationsChanged =
        !!settings && values.allowCancellations !== settings.allowCancellations;
      const cancellationPolicyUnchanged =
        values.cancellationPolicyText ===
        (settings?.cancellationPolicyText || '');

      if (allowCancellationsChanged && cancellationPolicyUnchanged) {
        const proceed = await confirm({
          message:
            "You've changed whether clients can cancel, but your cancellation policy text still reflects the old setting. Consider updating it so clients know what to expect.",
          title: 'Cancellation Policy Text',
          positiveText: 'Save',
          negativeText: 'Back',
          hideCloseButton: true,
          dismissible: false,
        });

        if (!proceed) return;
      }

      const applyToExistingUsers = await confirm({
        message:
          "Do you want to apply these changes to all existing users? Select yes or no below. If you select yes, we'll update all current users to have these settings",
        title: 'Apply to Existing Users?',
        positiveText: 'Yes, Apply to Current Users',
        negativeText: 'No',
        hideCloseButton: true,
        dismissible: false,
      });

      const reschedulePolicy = fromFormReschedulePolicy(
        values.reschedulePolicy,
        settings?.reschedulePolicy,
      );

      await updateBusinessSettings({
        ...values,
        reschedulePolicy,
        rescheduleFee:
          values.reschedulePolicyBehavior === ReschedulePolicyBehavior.FLAT_FEE
            ? values.rescheduleFee
            : null,
        applyToExistingUsers,
      });
    },
  };

  const generalManagersForm: FormData<GeneralManagersState> = {
    title: 'General Request Managers',
    initialValues: {
      managers: generalRequestManagerIds,
    },
    validationSchema: updateBusinessSchema,
    onSubmit: async values => {
      await updateBusiness({
        generalRequestManagerIds: values.managers,
      });
    },
    inputRows: [
      [
        {
          inputType: 'multiSelect',
          label: 'Shop managers',
          name: 'managers',
          options: shopManagers.map(etb => ({
            value: etb.employeeId,
            label:
              `${etb.user?.firstName ?? ''} ${
                etb.user?.lastName ?? ''
              }`.trim() || 'Unnamed',
          })),
        },
      ],
    ],
  };

  const stationMaxForm: FormData<StationMaxState> = {
    title: 'Tattoo Appointment Stations',
    initialValues: {
      stationMax: business.stationMax,
    },
    validationSchema: updateBusinessSchema,
    onSubmit: async values => {
      await updateBusiness({
        stationMax: values.stationMax || null,
      });
    },
    inputRows: [
      [
        {
          inputType: 'number',
          label: 'Number of stations',
          name: 'stationMax',
          placeholder: 'No limit',
        },
      ],
    ],
  };

  const bookAheadLimitText =
    settings?.bookAheadLimitDays && settings.bookAheadLimitDays <= 3
      ? `${settings.bookAheadLimitDays * 24} hours`
      : `${settings?.bookAheadLimitDays || 0} days`;
  const bookInAdvanceHours = Math.floor(
    (settings?.bookInAdvanceMinutes ?? 0) / 60,
  );
  const bookInAdvanceText =
    bookInAdvanceHours % 24 === 0
      ? `${bookInAdvanceHours / 24} day${
          bookInAdvanceHours / 24 > 1 ? 's' : ''
        }`
      : `${bookInAdvanceHours} hours`;

  return (
    <>
      <MinorSettings />

      <SectionDivider />

      {isEnabled && (
        <>
          <SettingsSection
            title="Default Artist Settings"
            subtitle="Update default artist settings. When you update these settings, you can choose to apply them to all existing artists, or just newly onboarded ones."
            isLoading={isLoadingSettings}
            rows={[
              {
                title: 'Appointment minimum',
                label: formatDollarAmount(settings?.minAppointmentPrice || 0),
              },
              {
                title: 'Max appts. per day',
                label: settings?.maxApptsPerDay
                  ? `${settings?.maxApptsPerDay} per day`
                  : 'No limit',
                tooltip: TOOLTIP.maxApptsPerDay,
              },
              {
                title: 'Book ahead limit',
                label: bookAheadLimitText,
                tooltip: TOOLTIP.bookAheadLimitDays,
              },
              {
                title: 'Book in advance',
                label: bookInAdvanceText,
                tooltip: TOOLTIP.bookInAdvanceHours,
              },
              {
                title: 'Smart Scheduling: Stack days',
                label: settings?.stackDays ? 'Yes' : 'No',
                tooltip: TOOLTIP.stackDays,
              },
              {
                title: 'Smart Scheduling: Stack times',
                label: settings?.stackTimes ? 'Yes' : 'No',
                tooltip: TOOLTIP.stackTimes,
              },
              {
                title: 'Allow cancellations',
                label: settings?.allowCancellations ? 'Yes' : 'No',
                tooltip: TOOLTIP.allowCancellations,
              },
              {
                title: 'Cancellation waitlist',
                label:
                  settings?.cancellationWaitlistEnabled ?? true
                    ? 'Enabled'
                    : 'Disabled',
                tooltip: TOOLTIP.cancellationWaitlistEnabled,
              },
              ...(settings?.cancellationWaitlistEnabled ?? true
                ? [
                    {
                      title: 'Waitlist notification style',
                      label:
                        WAITLIST_NOTIFY_MODE_LABELS[
                          settings?.cancellationWaitlistNotifyMode ??
                            CancellationWaitlistNotifyMode.INSTANT_ALL
                        ],
                      tooltip: TOOLTIP.cancellationWaitlistNotifyMode,
                    },
                  ]
                : []),
              {
                title: 'Cancellation policy',
                label:
                  settings?.cancellationPolicyText ||
                  'No cancellation policy set',
                tooltip: TOOLTIP.cancellationPolicyText,
              },
              {
                title: 'Allow clients to reschedule',
                label:
                  settings?.reschedulePolicy !== ReschedulePolicy.CLOSED
                    ? 'Yes'
                    : 'No',
                tooltip: TOOLTIP.reschedulePolicy,
              },
              {
                title: 'Number of free reschedules',
                label: `${settings?.maxCancellationTimes || 0} free reschedule${
                  settings?.maxCancellationTimes !== 1 ? 's' : ''
                }`,
                tooltip: TOOLTIP.maxCancellationTimes,
              },
              {
                title: 'Minimum notice to reschedule',
                label: `${settings?.minCancellationDays || 0} days`,
                tooltip: TOOLTIP.minCancellationDays,
              },
              {
                title: 'Policy when limits are exceeded',
                label: settings?.reschedulePolicyBehavior
                  ? RESCHEDULE_BEHAVIOR_LABELS[
                      settings.reschedulePolicyBehavior
                    ]
                  : RESCHEDULE_BEHAVIOR_LABELS.DEPOSIT,
                tooltip: TOOLTIP.reschedulePolicyBehavior,
              },
              ...(settings?.reschedulePolicyBehavior ===
              ReschedulePolicyBehavior.FLAT_FEE
                ? [
                    {
                      title: 'Reschedule fee',
                      label: formatDollarAmount(settings?.rescheduleFee || 0),
                      tooltip: TOOLTIP.rescheduleFee,
                    },
                  ]
                : []),
            ]}
            action={{
              type: 'button',
              text: 'Edit',
              onClick: () => setForm(defaultArtistSettingsForm),
            }}
          />
          <SectionDivider />
        </>
      )}

      <ServicesSettings />

      <SectionDivider />

      <SettingsSection
        title="General Requests"
        subtitle="General requests are a way for customers to submit a request to your studio without selecting a specific artist. Requests will be routed to shop managers (or anyone you choose below) to triage, and assign to an artist."
        rows={[
          {
            title: 'Allow general requests',
            label: 'Do you want to enable general requests?',
            action: {
              type: 'switch',
              checked: allowGeneralRequests,
              onChange: onToggleAllowGeneralRequests,
            },
          },
          {
            title: 'General request managers',
            label: generalManagersString,
            action: {
              type: 'button',
              text: 'Edit',
              onClick: () => setForm(generalManagersForm),
              disabled: !allowGeneralRequests,
            },
            tooltip:
              'Select which managers can manage general requests (requests with "Any Artist")',
          },
        ]}
        copy={{
          content: getBusinessShopUrl(business),
        }}
      />
      <SectionDivider />
      <SettingsSection
        title="Bookable Tattoo Stations"
        subtitle="If you have more artists than stations available, make sure you input the number of appointment stations you have so Porter can limit the number of appointments booked at the same time."
        rows={[
          {
            title: 'Tattoo appointment stations',
            label: business.stationMax
              ? `${business.stationMax} stations are bookable`
              : 'No limit on stations',
            tooltip:
              'If you have more artists than stations available, make sure you input the number of appointment stations you have so Porter can limit the number of appointments booked at the same time.',
            action: {
              type: 'button',
              text: 'Edit',
              onClick: () => setForm(stationMaxForm),
            },
          },
        ]}
      />
      <FormSmallModal form={form} onClose={() => setForm(null)} open={!!form} />
    </>
  );
}

export default BookingSettings;
